import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { Task } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import type { AiUsage, ConnectedOta, Review } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type {
  ReviewResponse,
  ReviewResponseMeta,
} from 'lumen/types/review-manager';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type ReviewsModel = {
  connectedOtas: ConnectedOta[];
  requestAiUsageTask?: Task<AiUsage | null, []>;
  requestReviewsTask: Task<ReviewResponse, []>;
};

// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  features: 'app.review_manager',
})
export default class HotelReviewsRoute extends Route {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare reviewDraftCache: Services['review-draft-cache'];
  @service declare store: Services['store'];

  requestAiUsageTask: Task<AiUsage | null, []> = task(async () => {
    try {
      return await this.store.queryRecord<AiUsage>('reviews/ai-usage', {
        typeKey: 'review-manager/ai/usage',
        namespace: APIGATEWAY,
        subscription_id: this.hotelAndMarketService.hotel.id!,
      });
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  requestConnectedOtasTask: Task<ConnectedOta[], []> = task(async () => {
    try {
      return await this.store.query<ConnectedOta>('reviews/connected-ota', {
        typeKey: 'review-manager/reviews/otas',
        namespace: APIGATEWAY,
        subscription_id: this.hotelAndMarketService.hotel.id!,
      });
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  requestReviewsTask: Task<ReviewResponse, []> = task(async () => {
    try {
      const result = await this.store.query<Review>('reviews/review', {
        typeKey: 'review-manager/reviews',
        namespace: APIGATEWAY,
        subscription_id: this.hotelAndMarketService.hotel.id!,
      });

      return {
        reviews: result,
        meta: result.meta as ReviewResponseMeta | undefined,
      };
    } catch (error) {
      return this.handleTaskErrorWithRetry(error);
    }
  });

  deactivate(): void {
    void this.requestReviewsTask.cancelAll();
    void this.requestConnectedOtasTask.cancelAll();

    if (this.access.hasReviewManagerReplyGenerateUpsell) {
      void this.requestAiUsageTask.cancelAll();
    }

    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }

    this.store.unloadAll('reviews/review');
    this.store.unloadAll('reviews/ai-reply');
    this.store.unloadAll('reviews/connected-ota');
    this.store.unloadAll('reviews/ai-usage');
    this.reviewDraftCache.clearAllDrafts();
  }

  private async handleTaskErrorWithRetry(error: unknown): Promise<never> {
    this.errorTracking.captureException(error);

    const confirmWasClicked = await this.alerting.showErrorAlert({
      title: this.intl.t('review_manager.error.load_reviews.title'),
      text: this.intl.t('review_manager.error.load_reviews.text'),
      buttons: {
        confirm: {
          text: this.intl.t('review_manager.error.load_reviews.confirm'),
        },
      },
      icon: '100x100_disconnected',
    });

    if (confirmWasClicked) {
      this.refresh();
    }

    throw new SilentTaskError(getErrorMessage(error));
  }

  async model(): Promise<ReviewsModel> {
    let connectedOtas: ConnectedOta[] = [];

    try {
      const result = await this.requestConnectedOtasTask.perform();
      connectedOtas = result;
    } catch {
      void this.alerting.showErrorAlert();
    }

    const model: ReviewsModel = {
      connectedOtas,
      requestReviewsTask: this.requestReviewsTask,
    };

    if (this.access.hasReviewManagerReplyGenerateUpsell) {
      model.requestAiUsageTask = this.requestAiUsageTask;
      void this.requestAiUsageTask.perform();
    }

    if (connectedOtas.length > 0) {
      void this.requestReviewsTask.perform();
    }

    return model;
  }
}
